/** Top-level navigation targets; nothing else is a destination (spec §2). */
export type Destination = "catalogs" | "browse" | "items" | "photos";

/**
 * The kind of view a route renders. This is the single chrome predicate
 * (spec §3): the shell derives the app bar, content header and nav from it
 * instead of from store navigation state.
 *
 * - `destination`: Catalogs, Browse root, Items, Photos — title only, no back,
 *   no crumbs.
 * - `container`: a container-shaped view with back + title + crumbs.
 * - `task`: Add, Print, Trash (back + title) and Scan (no content header).
 * - `search`: the search surface, its own `h1`, no content header.
 * - `unknown`: no route matches; the shell renders nothing special.
 */
export type Surface = "destination" | "container" | "task" | "search" | "unknown";

/**
 * The content header a route renders (spec §3). This replaces the old
 * page-specific chrome toggle: chrome is a function of the route only.
 *
 * - `title`: destination view — title only.
 * - `container`: back link + title + crumb trail.
 * - `task`: back link + title, no crumbs.
 * - `none`: the view owns its whole surface (search, scan).
 */
export type ContentHeaderKind = "title" | "container" | "task" | "none";

export interface RouteInfo {
  surface: Surface;
  /** The nav destination to highlight, or null when no destination owns it. */
  destination: Destination | null;
  /** The content header the route renders (spec §3). */
  header: ContentHeaderKind;
  /** The view owns a full-height content column (sticky footer) — scan. */
  fullHeight?: boolean;
  /**
   * Who renders the header. The shell renders every header except item detail:
   * only that screen holds the fetched item's name and owning container, so it
   * renders the same shared `ContentHeader` component itself (ticket 10).
   */
  headerOwner: "shell" | "screen";
}

const SHELL_HEADER = { headerOwner: "shell" } as const;

const UNKNOWN: RouteInfo = {
  surface: "unknown",
  destination: null,
  header: "none",
  ...SHELL_HEADER,
};

const DESTINATION = (destination: Destination): RouteInfo => ({
  surface: "destination",
  destination,
  header: "title",
  ...SHELL_HEADER,
});

const CONTAINER: RouteInfo = {
  surface: "container",
  destination: "browse",
  header: "container",
  ...SHELL_HEADER,
};

/** Item detail: container-shaped; its screen renders the shared content header. */
const ITEM_DETAIL: RouteInfo = { ...CONTAINER, headerOwner: "screen" };

const TASK_WITH_BACK: RouteInfo = {
  surface: "task",
  destination: null,
  header: "task",
  ...SHELL_HEADER,
};

const SEARCH: RouteInfo = {
  surface: "search",
  destination: null,
  header: "none",
  ...SHELL_HEADER,
};

/**
 * Map a pathname to its surface and chrome (spec §3). Pure, so the shell and
 * the tests share exactly one interpretation of the route table.
 *
 * Item detail keeps no top-level destination of its own: spec §2 selects
 * Browse on "an item detail" because the item's home is a container. Its
 * content is container-shaped, hence the `container` surface.
 *
 * @param pathname the router pathname, with or without a trailing slash.
 * @returns the route's surface, nav destination and content-header kind.
 */
export function resolveRoute(pathname: string): RouteInfo {
  const segments = pathname.split("/").filter((segment) => segment.length > 0);
  const [head, id] = segments;
  const depth = segments.length;

  if (!head) return DESTINATION("browse"); // `/` redirects to /browse
  if (depth > 2) return UNKNOWN;

  switch (head) {
    case "catalogs":
      return depth === 1 ? DESTINATION("catalogs") : UNKNOWN;
    case "browse":
      return depth === 1 ? DESTINATION("browse") : CONTAINER;
    case "home":
      return depth === 1 ? DESTINATION("browse") : UNKNOWN;
    case "detail":
      // Redirect-only (container-view 06): no chrome may flash before Navigate
      // replaces the legacy path with the merged container page.
      return UNKNOWN;
    case "edit":
      return depth === 2 && id ? CONTAINER : UNKNOWN;
    case "items":
      return depth === 1 ? DESTINATION("items") : id ? ITEM_DETAIL : UNKNOWN;
    case "gallery":
      return depth === 1 ? DESTINATION("photos") : UNKNOWN;
    case "search":
      return depth === 1 ? SEARCH : UNKNOWN;
    case "scan":
      // The camera fills the view: no content header, no back link (spec §3).
      return depth === 1 ? { ...TASK_WITH_BACK, header: "none", fullHeight: true } : UNKNOWN;
    case "add":
    case "print":
    case "trash":
      return depth === 1 || (head === "add" && depth === 2) ? TASK_WITH_BACK : UNKNOWN;
    default:
      return UNKNOWN;
  }
}
